import { DefaultGameState, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link MapComponent} */
const styles: string = css`
    :host {
        z-index: 5;
        display: none;
    }

    #inventory-screen {
        width: 100%;
        height: 100vh;
        position: fixed;
        left: 0;
        top: 0;
        
        display: flex;
        justify-content: center;
        flex-direction: column-reverse;
        align-items: center;
    }
    
    #close {
        background: blue;
        padding: 10px;
        margin-bottom: 10px;
        cursor: pointer;
    }
    
    #inventory-container {
        background: #312d45;
        
        width: 1000px;
        height: 400px;
        
        position: relative;

        display: flex;
        flex-wrap: wrap;
    }
    
    .inventory-slot {
        margin: 40px;
        width: 100px;
        height: 100px;
        background: #575469;
        
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .inventory-slot:nth-child(-n+3) {
        background: #807c99;
        border: 2px solid white;
        width: 98px;
        height: 98px;
    }
    .inventory-slot:nth-child(-n+6) {
        margin-top: 81px;
    }

    .inventory-slot img {
        width: 60%;
        height: 60%;
    }
`;

export class InventoryComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);
            return;
        }

        // Reset the component
        this._currentGameState = state;

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderMap()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private moveToStart<T>(arr: T[], value: T): T[] {
        const index: number = arr.indexOf(value);
        if (index > 0) {
            // Ignore if already at index 0 or not found
            arr.unshift(arr.splice(index, 1)[0]);
        }
        return arr;
    }

    private renderMap(): HTMLElement {
        const inventory: string[] | undefined = this._currentGameState?.inventory;
        // Set timeout to make changes to map frontend after its loaded in
        setTimeout(() => {
            const closeDiv: HTMLDivElement = this.shadowRoot!.getElementById("close") as HTMLDivElement;
            closeDiv.addEventListener("click", () => {
                this.style.display = "none";
                this.updateGameState({ type: "switch-page", page: Page.Canvas } as GameState);
            });

            if (inventory) {
                for (let i: number = 0; i < inventory.length; i++) {
                    const inventorySlot: HTMLElement = this.shadowRoot!.getElementById(`inventory-slot${i}`) as HTMLDivElement;
                    inventorySlot.addEventListener("click", async () => {
                        const newInventory: string[] | undefined = this.moveToStart(inventory, inventorySlot.classList[1]);
                        const newState: GameState | undefined = await this._gameRouteService.updateInventory(newInventory);
                        if (!newState) {
                            return;
                        }
                        this.updateGameState(newState);
                    });
                }
            }
        });
        return html`
                    <section id="inventory-screen">
                        <div id="inventory-container">
                            <p style="position: absolute; top: 0; left: 0; width: 100%; text-align: center; height: 20px;">Click on items to send them to your hotbar</p>
                            ${inventory
                                ? inventory.map((item, index) => `
                                <div id="inventory-slot${index}" class="inventory-slot ${item}">
                                    <img src="/assets/img/items/${item}.png" />
                                </div>`).join("")
                                : ""}
                        </div>
                        <div id="close" class="button">Close</div>
                    </section>


        `;
    }
}
